import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Menu,
    X,
    BookOpen,
    Code,
    FileText,
    Mail,
    Camera,
    ChevronDown,
    ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";

const navigation = [
    { name: "Home", href: "/", icon: BookOpen },
    { name: "Research", href: "/research", icon: Code },
    { name: "Publications", href: "/publications", icon: BookOpen },
    { name: "Contact", href: "/contact", icon: Mail },
];

const resourceLinks = [
    { name: "Professional CV", href: "/cv-preview", icon: FileText, desc: "Academic Resume" },
    { name: "Gallery", href: "/gallery", icon: Camera, desc: "Activity Highlights" },
];

export function Header() {
    const { user, isSignedIn } = useAuth();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 text-slate-900 ${isScrolled
                ? "bg-[#fbfbfa]/95 backdrop-blur-xl border-b border-stone-200"
                : "bg-[#fbfbfa]/85 backdrop-blur-md border-b border-transparent"
                }`}
        >
            <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16 lg:h-20">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full overflow-hidden border border-stone-200 shadow-sm group-hover:border-stone-300 transition-all duration-300 bg-white">
                            <img
                                src="/michael-matonya.png"
                                alt="Michael Matonya Logo"
                                className="w-full h-full object-cover object-top scale-110"
                            />
                        </div>
                        <div className="flex flex-col">
                            <span className="text-lg lg:text-xl font-semibold text-slate-900 leading-none transition-colors">
                                Michael Matonya
                            </span>
                            <span className="text-[11px] font-medium text-slate-500 mt-1">
                                Industrial engineer
                            </span>
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <nav className="hidden lg:flex items-center gap-1">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${location.pathname === item.href
                                    ? "text-slate-800 bg-stone-50 border border-stone-200"
                                    : "text-slate-500 hover:text-slate-900 hover:bg-stone-100"
                                    }`}
                            >
                                {item.name}
                            </Link>
                        ))}

                        {/* Resources Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-500 hover:text-slate-900 hover:bg-stone-100 transition-all outline-none">
                                    Resources <ChevronDown className="w-4 h-4 opacity-50" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-64 p-2 bg-[#fbfbfa]/95 backdrop-blur-xl border-stone-200 rounded-xl shadow-lg z-50">
                                {resourceLinks.map((item) => (
                                    <DropdownMenuItem key={item.name} asChild className="p-0 mb-1 last:mb-0">
                                        <Link
                                            to={item.href}
                                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-stone-100 transition-colors"
                                        >
                                            <div className="w-10 h-10 rounded-lg bg-white border border-stone-200 flex items-center justify-center shrink-0">
                                                <item.icon className="w-5 h-5 text-slate-600" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold text-slate-900">{item.name}</div>
                                                <div className="text-xs text-slate-500">{item.desc}</div>
                                            </div>
                                        </Link>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </nav>

                    {/* Actions */}
                    <div className="flex items-center gap-2 sm:gap-4">
                        {/* Auth Button */}
                        <div className="hidden sm:block">
                            {isSignedIn ? (
                                <Link to="/dashboard">
                                    <Button variant="outline" className="rounded-full px-5 border-stone-200 hover:border-stone-300 hover:bg-stone-50">
                                        <div className="w-6 h-6 rounded-full overflow-hidden mr-2 border border-stone-200">
                                            <img src={user?.avatar_url} alt={user?.full_name} className="w-full h-full object-cover" />
                                        </div>
                                        Dashboard
                                    </Button>
                                </Link>
                            ) : (
                                <Link to="/sign-in">
                                    <Button variant="default" className="rounded-full px-6">
                                        Sign In
                                    </Button>
                                </Link>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                        >
                            {isMobileMenuOpen ? (
                                <X className="w-6 h-6" />
                            ) : (
                                <Menu className="w-6 h-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Dropdown */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        {/* Overlay Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-sm lg:hidden"
                        />

                        {/* Dropdown Menu */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -20 }}
                            transition={{ type: "spring", duration: 0.4, bounce: 0 }}
                            className="fixed top-20 left-4 right-4 z-50 lg:hidden overflow-hidden bg-white rounded-3xl border border-slate-200 shadow-2xl shadow-indigo-500/10"
                        >
                            <nav className="p-6 space-y-6">
                                {/* Auth Section Mobile */}
                                <div className="pb-4 border-b border-slate-100">
                                    {isSignedIn ? (
                                        <Link to="/dashboard" className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50 border border-slate-100">
                                            <img src={user?.avatar_url} className="w-10 h-10 rounded-full border border-slate-200" />
                                            <div>
                                                <div className="text-sm font-bold text-slate-900">{user?.full_name}</div>
                                                <div className="text-[10px] text-slate-500">Go to Dashboard</div>
                                            </div>
                                        </Link>
                                    ) : (
                                        <div className="grid grid-cols-2 gap-3">
                                            <Button variant="outline" asChild className="rounded-xl border-slate-200">
                                                <Link to="/sign-in">Sign In</Link>
                                            </Button>
                                            <Button variant="default" asChild className="rounded-xl bg-slate-900 text-white">
                                                <Link to="/sign-up">Sign Up</Link>
                                            </Button>
                                        </div>
                                    )}
                                </div>
                                <div className="space-y-1">
                                    <h4 className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Main Navigation</h4>
                                    {navigation.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-base font-semibold transition-all ${location.pathname === item.href
                                                ? "text-slate-800 bg-stone-50 border border-stone-200"
                                                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                                                }`}
                                        >
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${location.pathname === item.href ? "bg-white border border-stone-200" : "bg-slate-100"
                                                }`}>
                                                {item.icon && <item.icon className={`w-4 h-4 ${location.pathname === item.href ? "text-slate-700" : "text-slate-500"}`} />}
                                            </div>
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>

                                {/* Actions/Products Section */}
                                <div className="space-y-1">
                                    <h4 className="px-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Professional Resources</h4>
                                    {resourceLinks.map((item) => (
                                        <Link
                                            key={item.name}
                                            to={item.href}
                                            className="flex items-center gap-3 px-4 py-3 rounded-2xl text-base font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                                                {item.icon && <item.icon className="w-4 h-4 text-slate-500" />}
                                            </div>
                                            {item.name}
                                        </Link>
                                    ))}
                                </div>
                            </nav>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </header>
    );
}
